import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import './AddMenuButton.scss';
import { useAddVulOnMapModalStore } from './Modals/AddVulOnMap/AddVulOnMapModalStore';
import { useAddEncounterOnMapModalStore } from './Modals/AddEncOnMap/AddEncounterOnMapModalStore';

interface AddMenuButtonProps {
  setIsAddMctModalOpen: (isOpen: boolean) => void;
  setIsAddSRSTModalOpen: (isOpen: boolean) => void;
  setIsAddSMSTModalOpen: (isOpen: boolean) => void;
  setIsAddSRBMModalOpen: (isOpen: boolean) => void;
  setIsAddHubModalOpen: (isOpen: boolean) => void;
  objectSlug: string;
  planSlug: string;
  isActiveFilterArchive?: boolean;
}

export const AddMenuButton: React.FC<AddMenuButtonProps> = ({
  setIsAddMctModalOpen,
  setIsAddSRSTModalOpen,
  setIsAddSMSTModalOpen,
  setIsAddSRBMModalOpen,
  setIsAddHubModalOpen,
  objectSlug,
  planSlug,
  isActiveFilterArchive,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showDevicesSubmenu, setShowDevicesSubmenu] = useState(false);
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { onManageAddVulOnMapOpen } = useAddVulOnMapModalStore();
  const { onManageAddEncounterOnMapOpen } = useAddEncounterOnMapModalStore();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowDevicesSubmenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDeviceSelect = (type: 'mct' | 'srbm' | 'srst' | 'smst') => {
    if (type === 'mct') {
      setIsAddMctModalOpen(true);
    } else if (type === 'srst') {
      setIsAddSRSTModalOpen(true);
    } else if (type === 'smst') {
      setIsAddSMSTModalOpen(true);
    } else {
      setIsAddSRBMModalOpen(true);
    }
    setIsOpen(false);
    setShowDevicesSubmenu(false);
  };

  const handleHubClick = () => {
    setIsAddHubModalOpen(true);
    setIsOpen(false);
  };

  const handleVulClick = () => {
    onManageAddVulOnMapOpen(objectSlug, planSlug);
    setIsOpen(false);
  };

  const handleEncounterClick = () => {
    onManageAddEncounterOnMapOpen(objectSlug, planSlug);
    setIsOpen(false);
  };

  return (
    <div title="Add Marker" ref={dropdownRef} style={{ position: 'absolute', top: 85, left: 10, zIndex: 1000 }}>
      <button
        style={{
          width: 34,
          height: 34,
          borderRadius: 4,
          border: '2px solid #6f42c1',
          background: isHovered ? '#6f42c1' : '#fff',
          fontSize: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          if (isActiveFilterArchive) {
            // If archive is active, show alert instead of opening menu
            enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
          } else {
            setIsOpen(prev => !prev);
          }
        }}
      >
        <i className="ri-menu-add-line fs-20" style={{ color: isHovered ? '#fff' : '#6c3fc5' }} />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '100%',
            minWidth: 220,
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            marginRight: '8px',
            overflow: 'hidden',
          }}
        >
          <div
            className="menu-item"
            style={{
              padding: 10,
              cursor: 'pointer',
              color: '#6c3fc5',
              borderBottom: '1px solid #eee',
              transition: 'background-color 0.2s',
            }}
            onClick={handleHubClick}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {t('InteractiveMap.addMenu.hub')}
          </div>

          <div
            className="menu-item"
            style={{
              padding: 10,
              cursor: 'pointer',
              color: '#6c3fc5',
              position: 'relative',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={() => setShowDevicesSubmenu(true)}
            onMouseLeave={() => setShowDevicesSubmenu(false)}
          >
            <span>{t('InteractiveMap.addMenu.devices')}</span>
            <i className="ri-arrow-right-s-line" />
          </div>

          <div
            className="menu-item"
            style={{
              padding: 10,
              cursor: 'pointer',
              color: '#6c3fc5',
              borderBottom: '1px solid #eee',
              transition: 'background-color 0.2s',
            }}
            onClick={handleVulClick}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {t('InteractiveMap.addMenu.vulnerabilities')}
          </div>

          <div
            className="menu-item"
            style={{
              padding: 10,
              cursor: 'pointer',
              color: '#6c3fc5',
              transition: 'background-color 0.2s',
            }}
            onClick={handleEncounterClick}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {t('InteractiveMap.addMenu.pestEncounters')}
          </div>
        </div>
      )}

      {/* Submenu rendered outside main popup */}
      {showDevicesSubmenu && (
        <div
          style={{
            position: 'absolute',
            top: '40px',
            left: '254px',
            minWidth: 180,
            background: '#fff',
            border: '1px solid #eee',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
          }}
          onMouseEnter={() => setShowDevicesSubmenu(true)}
          onMouseLeave={() => setShowDevicesSubmenu(false)}
        >
          <div
            className="menu-item hover-effect"
            style={{
              padding: 10,
              color: '#6c3fc5',
              cursor: 'pointer',
              borderBottom: '1px solid #eee',
              transition: 'background-color 0.2s',
            }}
            onClick={() => handleDeviceSelect('mct')}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {t('InteractiveMap.addMenu.multiCatchTrap')}
          </div>
          <div
            className="menu-item hover-effect"
            style={{
              padding: 10,
              color: '#6c3fc5',
              cursor: 'pointer',
              borderBottom: '1px solid #eee',
              transition: 'background-color 0.2s',
            }}
            onClick={() => handleDeviceSelect('srst')}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {t('InteractiveMap.addMenu.snapTrapRat')}
          </div>
          <div
            className="menu-item hover-effect"
            style={{
              padding: 10,
              color: '#6c3fc5',
              cursor: 'pointer',
              borderBottom: '1px solid #eee',
              transition: 'background-color 0.2s',
            }}
            onClick={() => handleDeviceSelect('smst')}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {t('InteractiveMap.addMenu.snapTrapMouse')}
          </div>
          <div
            className="menu-item hover-effect"
            style={{
              padding: 10,
              color: '#6c3fc5',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onClick={() => {
              setIsAddSRBMModalOpen(true);
              setIsOpen(false);
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {t('InteractiveMap.addMenu.smartRodentBaitModule')}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddMenuButton;
